/**
 * The derived role `textLabel` (TMMAPP-0099, Operator 2026-10-05; resolved like `raisedLine`). The
 * prototype sets its small labels — eyebrows, overlines, kind labels, menu headers, dialog section
 * labels, meta captions, helper text — in its lightest grey, `text3`, as TEXT. saas-auth guarantees
 * `text3` only at 3:1, so the label colour is derived per mode and per canvas: the served `text3`
 * where it measures at least 4.5:1 on that canvas, otherwise `text2` (guaranteed 4.5:1 on every
 * canvas). Size, weight, case and spacing stay as drawn. It is a derived value, proved by
 * construction and measured in `contrastAudit.test.ts`; it adds nothing to the served pair list
 * (`brandGuarantee.test.ts` stays saas-auth's mirror).
 */
import type { BrandRoles } from './brandTheme';

/** The canvases a label sits on (`surface2`: the v2 sidebar, TMMAPP-0151 §A 5). */
export const LABEL_CANVASES = ['bg', 'surface', 'surface2', 'surfaceRaised'] as const;
export type LabelCanvas = (typeof LABEL_CANVASES)[number];

/** The text threshold a label must meet (WCAG AA, normal text). */
export const LABEL_MIN_CONTRAST = 4.5;

type LabelRoles = Pick<BrandRoles, 'text3' | 'text2' | LabelCanvas>;

interface Rgba {
  r: number;
  g: number;
  b: number;
  a: number;
}

function parse(color: string): Rgba | null {
  const match = /^#([\da-f]{3}|[\da-f]{6}|[\da-f]{8})$/i.exec(color.trim());
  if (match === null) return null;
  const body = match[1] ?? '';
  const full = body.length === 3 ? [...body].map((c) => c + c).join('') : body;
  const channel = (i: number) => Number.parseInt(full.slice(i, i + 2), 16);
  return {
    r: channel(0),
    g: channel(2),
    b: channel(4),
    a: full.length === 8 ? channel(6) / 255 : 1,
  };
}

function luminance({ r, g, b }: Rgba): number {
  const linear = (value: number) => {
    const s = value / 255;
    return s <= 0.04045 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * linear(r) + 0.7152 * linear(g) + 0.0722 * linear(b);
}

/** WCAG contrast of `foreground` over the opaque `canvas` (a translucent ink is composited first). */
export function labelContrast(foreground: string, canvas: string): number {
  const fg = parse(foreground);
  const bg = parse(canvas);
  if (fg === null || bg === null) return 0;
  const mix = (f: number, b: number) => Math.round(f * fg.a + b * (1 - fg.a));
  const ink = { r: mix(fg.r, bg.r), g: mix(fg.g, bg.g), b: mix(fg.b, bg.b), a: 1 };
  const lighter = Math.max(luminance(ink), luminance(bg));
  const darker = Math.min(luminance(ink), luminance(bg));
  return (lighter + 0.05) / (darker + 0.05);
}

/**
 * A translucent `layer` (the `hover` / `selected` state layer) painted over the opaque `canvas`,
 * as one opaque hex colour; a value that isn't hex comes back as `canvas`.
 */
export function flattenLayer(layer: string, canvas: string): string {
  const fg = parse(layer);
  const bg = parse(canvas);
  if (fg === null || bg === null) return canvas;
  const mix = (f: number, b: number) => Math.round(f * fg.a + b * (1 - fg.a));
  const hex = (value: number) => value.toString(16).padStart(2, '0');
  return `#${hex(mix(fg.r, bg.r))}${hex(mix(fg.g, bg.g))}${hex(mix(fg.b, bg.b))}`;
}

/** The label colour on `canvas`: `text3` at 4.5:1 or better there, else `text2`. */
export function resolveTextLabel(roles: LabelRoles, canvas: LabelCanvas): string {
  return labelContrast(roles.text3, roles[canvas]) >= LABEL_MIN_CONTRAST
    ? roles.text3
    : roles.text2;
}

const cache = new Map<string, (canvas: LabelCanvas) => string>();

/**
 * `textLabel(canvas)` for one mode's roles. The function is shared by every token set with the same
 * roles, so two equal token sets stay equal (`toEqual`) and a theme rebuild reuses it.
 */
export function textLabelFor(roles: LabelRoles): (canvas: LabelCanvas) => string {
  const key = [roles.text3, roles.text2, ...LABEL_CANVASES.map((canvas) => roles[canvas])].join(
    '|',
  );
  const hit = cache.get(key);
  if (hit !== undefined) return hit;
  const resolved = Object.fromEntries(
    LABEL_CANVASES.map((canvas) => [canvas, resolveTextLabel(roles, canvas)]),
  ) as Record<LabelCanvas, string>;
  const fn = (canvas: LabelCanvas) => resolved[canvas];
  cache.set(key, fn);
  return fn;
}

/**
 * The CSS custom property a canvas sets to its `textLabel`; a label reads the nearest one. The name
 * must never end in `label`: emotion reads `label:<value>` in a style as the class-name label, so
 * `--…-label:#hex` would put a `#` in the class and void the whole rule.
 *
 * A label is derived on a plain canvas only. Inside a `hover` or `selected` layer (a hovered or
 * selected row) it steps up to `text2`, which is guaranteed under both.
 */
export const TEXT_LABEL = '--tmm-label-ink';

/**
 * The `sx` that makes a box a label canvas: a non-Paper box painting `surface` (a wizard card, a
 * footer, a segmented track, a drag overlay) sets the `surface` label so the labels inside it are
 * measured against what they sit on.
 */
export function labelCanvasSx(
  tokens: { textLabel: (canvas: LabelCanvas) => string },
  canvas: LabelCanvas,
): Record<string, string> {
  return { [TEXT_LABEL]: tokens.textLabel(canvas) };
}
